import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useMaterialUIController } from 'context';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import CalendarRoot from 'examples/Calendar/CalendarRoot';
import CircularProgress from '@mui/material/CircularProgress';
import {
  setCalendarEvent,
  resetCalendarEvent,
} from './store/calendarEventSlice';
import { LightTooltip } from 'components/Ui/styled';
import EventDialog from './EventDialog';

const DashboardCalendar = () => {
  const dispatch = useDispatch();
  const [calendarEventsData, setCalendarEventsData] = useState([]);
  const [tooltip, setTooltip] = useState(false);
  const [eventDetailsLoading, setEventDetailsLoading] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const calendarEvents = useSelector(
    (state) => state?.dashboard?.calendarEvents || []
  );
  const calendarCategories = useSelector(
    (state) => state?.dashboard?.calendarCategories || []
  );

  const handleEvents = (arg) => {
    console.log('handleEvents', arg);
  };

  const handleClick = () => {
    console.log('handleClick');
  };

  const fetchCalendar = useCallback(() => {
    const getCategoryColor = (categoryId) => {
      const getColor = calendarCategories?.find(
        (item) => item.categoryId === categoryId
      );
      return getColor?.color;
    };
    const eventsData =
      calendarEvents?.map((item) => {
        const isAllDay = item.isAllDayEvent;
        let start, end;

        if (isAllDay) {
          // For all-day events
          start = item.startDate;
          end = moment(item.endDate).add(1, 'day').format('YYYY-MM-DD');
        } else {
          // For timed events - create ISO datetime strings with timezone
          start = moment(
            `${item.startDate} ${item.startTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format();
          end = moment(
            `${item.endDate} ${item.endTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format();
        }

        return {
          id: item.eventId,
          title: item.title,
          start: start,
          end: end,
          allDay: isAllDay,
          backgroundColor: getCategoryColor(item.categoryId),
          borderColor: getCategoryColor(item.categoryId),
          extendedProps: {
            endTime: item.endTime,
            eventId: item.eventId,
            location: item.location,
            startTime: item.startTime,
            categoryId: item.categoryId,
            description: item.description,
          },
        };
      }) || [];
    setCalendarEventsData(eventsData);
  }, [calendarEvents, calendarCategories]);

  useEffect(() => {
    if (calendarEvents) {
      fetchCalendar();
    }
  }, [calendarEvents, fetchCalendar]);

  const handleEventMouseEnter = async (eventInfo) => {
    setTooltip(true);
    const calendarEvent = calendarEvents?.find(
      (item) => item?.eventId === eventInfo?.event?.extendedProps?.eventId
    );
    setEventDetailsLoading(true);
    dispatch(setCalendarEvent(calendarEvent));
    setEventDetailsLoading(false);
    return calendarEvent;
  };

  const handleEventMouseLeave = () => {
    dispatch(resetCalendarEvent());
    setTooltip(false);
  };

  return (
    <>
      <CalendarRoot
        p={2}
        ownerState={{ darkMode }}
        sx={{
          height: '510px',
          overflow: 'auto',

          '&::-webkit-scrollbar': {
            width: '9px', // vertical scrollbar width
            height: '9px', // horizontal scrollbar height
          },
          '::-webkit-scrollbar-thumb': {
            background: `#dcdcdc`,
            borderRadius: '10px',
          },
          '& .fc-toolbar-title': {
            whiteSpace: 'nowrap',
            display: 'inline-block',
            fontSize: '1rem !important',
            fontWeight: '500 !important',
          },
          '& .fc-toolbar': {
            flexWrap: 'nowrap !important',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          '& .fc-toolbar-chunk': {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          },
          '& .fc-button': {
            fontSize: '0.8rem',
            padding: '2px 6px',
            borderRadius: '8px',
            textTransform: 'capitalize',
          },

          // ADD THESE STYLES TO REDUCE DATE ROW SIZE
          '& .fc-daygrid-day-frame': {
            minHeight: '65px !important',
            height: '65px !important',
          },
          '& .fc-daygrid-day-top': {
            padding: '1px 2px !important',
            fontSize: '0.7rem !important',
          },
          '& .fc-daygrid-day-number': {
            fontSize: '0.75rem !important',
            padding: '1px !important',
          },
          '& .fc-daygrid-event': {
            fontSize: '0.65rem !important',
            margin: '0.5px 0 !important',
            minHeight: '14px !important',
            lineHeight: '1 !important',
          },
          '& .fc-daygrid-event-harness': {
            marginTop: '0px !important',
          },
          '& .fc-daygrid-day-events': {
            marginTop: '1px !important',
            minHeight: 'auto !important',
          },

          // reduce overall font size
          '& .fc-timegrid': {
            fontSize: '0.75rem !important',
          },

          // Time labels (12am, 1am, etc.)
          '& .fc-timegrid-axis': {
            fontSize: '0.7rem !important',
            color: '#555',
          },

          // Event title text
          '& .fc-timegrid-event .fc-event-title': {
            fontSize: '0.7rem !important',
            padding: '1px 2px !important',
            lineHeight: '1.1 !important',
          },

          // All-day event bar (top row)
          '& .fc-timegrid-allday .fc-event': {
            fontSize: '0.7rem !important',
            padding: '1px 3px !important',
          },

          // Column headers (Mon, Tue, etc.)
          '& .fc-col-header-cell-cushion': {
            fontSize: '0.75rem !important',
            fontWeight: '500 !important',
            padding: '2px !important',
          },

          // Time labels (left side)
          '& .fc-timegrid-slot-label': {
            fontSize: '0.7rem !important',
            padding: '0 2px !important',
          },
        }}
      >
        <FullCalendar
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek',
          }}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          firstDay={1}
          views={{
            dayGridMonth: {
              displayEventTime: false,
            },
            timeGridWeek: {
              type: 'timeGridWeek',
              buttonText: 'Week',
              titleFormat: { month: 'short', day: 'numeric', year: 'numeric' },
              weekends: false,
              displayEventTime: true,
              slotMinTime: '07:00:00',
              slotMaxTime: '24:00:00',
              visibleRange: (currentDate) => {
                const start = moment(currentDate).startOf('week').add(1, 'day');
                const end = moment(start).add(4, 'days');
                return { start: start.toDate(), end: end.toDate() };
              },
              dayHeaderFormat: { day: 'numeric', weekday: 'short' },
            },
          }}
          events={calendarEventsData}
          displayEventTime={false}
          datesSet={handleEvents}
          selectable
          select={handleClick}
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          height="auto"
          timeZone="local"
          showNonCurrentDates={true}
          fixedWeekCount={false}
          eventDisplay="block"
        />
      </CalendarRoot>
      {tooltip && (
        <>
          <LightTooltip
            title={
              eventDetailsLoading ? (
                <CircularProgress color="inherit" />
              ) : (
                <EventDialog />
              )
            }
            open={tooltip}
            placement="left-end"
          >
            <div />
          </LightTooltip>
        </>
      )}
    </>
  );
};

export default DashboardCalendar;
